import { Checkbox } from '@liveauctioneers/hammer-ui-core/checkbox';
import { Paperclip, User } from '@liveauctioneers/hammer-ui-core/icons';
import { BodySecondary, Caption, H5, H6 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { isToday, isYesterday } from 'date-fns';
import { memo, MouseEvent, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { safeFromUnixTime, shortFormatDate, timeOnlyDate } from '@/utils/dates';
import { safelyRender } from '@/utils/safelyRender';

import { ConversationType } from '../types/Conversation';

type Props = {
    readonly getMessengerUrl: Function;
    readonly handleSelect: (e: MouseEvent<HTMLInputElement>) => void;
    readonly mobile?: boolean;
    readonly selected: boolean;
    testid?: string;
    readonly thread: ConversationType;
    readonly yesterdayText?: ReactNode;
};

const ListItemComponent = ({
    getMessengerUrl,
    handleSelect,
    mobile,
    selected,
    testid = 'messengerListItem',
    thread,
    yesterdayText = 'Yesterday',
}: Props) => {
    const navigate = useNavigate();
    const {
        conversationId,
        hasAttachments,
        imageUrl,
        logoUrl,
        messageCount,
        read,
        sender,
        subject,
        tsLastMessageSent,
        type,
    } = thread;
    const messengerChatUrl = getMessengerUrl(conversationId);
    const messageSentDate = safeFromUnixTime(tsLastMessageSent);
    let date: ReactNode = shortFormatDate(tsLastMessageSent);

    if (isToday(messageSentDate)) {
        date = timeOnlyDate(tsLastMessageSent);
    } else if (isYesterday(messageSentDate)) {
        date = yesterdayText;
    }

    const imageContainerClass =
        'relative mr-[20px] flex h-[32px] min-h-[32px] w-[32px] min-w-[32px] items-center justify-center self-center bg-white [&_img]:max-h-[30px] [&_img]:max-w-[30px]';

    let avatar: ReactNode;

    if (imageUrl) {
        avatar = (
            <div className={imageContainerClass}>
                <img
                    loading="lazy"
                    src={imageUrl}
                />
            </div>
        );
    } else if (type === 'bidder') {
        avatar = (
            <div className="relative mr-[20px] flex h-[32px] w-[32px] items-center justify-center self-center overflow-hidden rounded-full bg-primary-500 text-center [&_svg]:absolute [&_svg]:bottom-[-4px]">
                <User
                    color="inverse"
                    size="md"
                    style="fill"
                />
            </div>
        );
    } else if (type === 'house') {
        avatar = (
            <div className={imageContainerClass}>
                <img
                    loading="lazy"
                    src={logoUrl}
                />
            </div>
        );
    }

    return (
        <tr
            className={cn(
                'cursor-pointer border-t border-border-secondary',
                'last:border-b last:border-b-transparent',
                '[&_td]:border-b-0 [&_td]:border-t-0',
                'hover:border-b hover:border-b-primary-400 hover:border-t-primary-400',
                '[&:hover_td:first-of-type]:border-l [&:hover_td:first-of-type]:border-l-primary-400',
                '[&:hover_td:last-of-type]:border-r [&:hover_td:last-of-type]:border-r-primary-400',
                read ? 'bg-surface-100' : 'bg-white'
            )}
            data-testid={testid}
            onClick={() => navigate(messengerChatUrl)}
            role="button"
        >
            {!mobile && (
                <td>
                    <Checkbox.Option
                        aria-label={`Select ${sender} - Subject: ${subject}`}
                        checked={selected}
                        className="max-[699px]:hidden m-0 p-0 pl-xs"
                        name={`${conversationId}`}
                        onChange={handleSelect}
                    />
                </td>
            )}
            <td>
                <div className="flex flex-row">
                    {avatar}
                    <div className="flex flex-col justify-center no-underline hover:no-underline [&>span]:block [&_h6]:flex">
                        <div className="flex flex-row">
                            {!read ? (
                                <H5>
                                    {safelyRender({
                                        html: sender,
                                        wrapper: 'span',
                                    })}
                                </H5>
                            ) : (
                                <BodySecondary color="secondary">
                                    {safelyRender({
                                        html: sender,
                                        wrapper: 'span',
                                    })}
                                </BodySecondary>
                            )}
                            {messageCount > 1 && (
                                <Caption
                                    className={cn('mb-[1px] ml-[3px] self-end', !read && 'hui-text-label-strong')}
                                    color={read ? 'secondary' : undefined}
                                >
                                    &nbsp;{messageCount}
                                </Caption>
                            )}
                        </div>
                        {!read ? (
                            <H6 color="secondary">
                                {safelyRender({
                                    html: subject,
                                    wrapper: 'span',
                                })}
                            </H6>
                        ) : (
                            <BodySecondary color="secondary">
                                {safelyRender({
                                    html: subject,
                                    wrapper: 'span',
                                })}
                            </BodySecondary>
                        )}
                    </div>
                </div>
            </td>

            <td className="table-cell">{hasAttachments && <Paperclip size="sm" />}</td>
            {!mobile && (
                <td className="table-cell whitespace-nowrap pb-[6px] pl-[6px] pr-xs pt-[6px]">
                    <div className="max-[699px]:!hidden ml-[20px] text-end">
                        {!read ? (
                            <H6 color="secondary">{date}</H6>
                        ) : (
                            <BodySecondary color="secondary">{date}</BodySecondary>
                        )}
                    </div>
                </td>
            )}
        </tr>
    );
};

export const ListItem = memo<Props>(ListItemComponent);
